// import { useState, useEffect } from 'react';
// import axios from 'axios';
// import { useTrendsContext } from '../contexts/TrendsContext.js';
// import { API_URLS } from '../api/config.js';

// export const useTrendData = (analysisData, tabKey, shouldFetch = true) => {
//     const [loading, setLoading] = useState(false);
//     const [error, setError] = useState(null);
//     const { trendsDataCache, updateTrendsCache } = useTrendsContext();
  
//     useEffect(() => {
//       const fetchData = async () => {
//         if (!shouldFetch || !analysisData) return;
//         if (trendsDataCache[tabKey]) return; // 캐시된 데이터가 있으면 요청하지 않음
  
//         try {
//           setLoading(true);
//           setError(null);
  
//           const requestBody = {
//             cat_id: analysisData.cat_id,
//             group_ids_1: analysisData.group_ids_1,
//             options: {
//               include_raw_data: true,
//               time_scale: "M"
//             },
//             tab: tabKey,
//             ...(analysisData.group_ids_2 && { group_ids_2: analysisData.group_ids_2 })
//           };
  
//           const response = await axios({
//             method: 'post',
//             url: `${API_URLS.API}/trends/metrics`,
//             headers: { 'Content-Type': 'application/json' },
//             data: requestBody,
//             timeout: 30000
//           });
//           console.log("metrics 요청");
//           updateTrendsCache(tabKey, response.data);
//         } catch (err) {
//           setError(err.response?.data?.message || err.message);
//         } finally {
//           setLoading(false);
//         }
//       };
  
//       fetchData();
//     }, [analysisData, tabKey, shouldFetch, trendsDataCache, updateTrendsCache]);
  
//     return { data: trendsDataCache[tabKey], loading, error };
//   };

import { useState, useEffect } from 'react';
import axios from 'axios';
import { useTrendsContext } from '../contexts/TrendsContext.js';
import { useLanguage } from '../contexts/LanguageContext.js';
import { API_URLS } from '../api/config.js';
import { applyBrandNameCorrections } from '../utils/brandNameCorrections.js';

export const useTrendData = (analysisData, tabKey, shouldFetch = true, retryTrigger = 0) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { trendsDataCache, updateTrendsCache } = useTrendsContext();
    const { language } = useLanguage();

    // Create cache key that includes language to separate data by language
    const cacheKey = `${tabKey}_${language}`;
  
    useEffect(() => {
      const fetchData = async () => {
        if (!shouldFetch || !analysisData) return;
        if (trendsDataCache[cacheKey]) return; // Check for language-specific cached data

        const startTime = Date.now();

        try {
          setLoading(true);
          setError(null);

          const requestBody = {
            cat_id: analysisData.cat_id,
            group_ids_1: analysisData.group_ids_1,
            options: {
              include_raw_data: true,
              time_scale: "M"
            },
            tab: tabKey,
            ...(analysisData.group_ids_2 && { group_ids_2: analysisData.group_ids_2 })
          };

          // Add language as query parameter
          const params = { lang: language };

          const response = await axios({
            method: 'post',
            url: `${API_URLS.API}/trends/metrics`,
            headers: { 'Content-Type': 'application/json' },
            data: requestBody,
            params: params, // Language as query parameter
            timeout: 60000 // Increased to 60 seconds
          });

          console.log(`metrics 요청 (language: ${language})`);
          updateTrendsCache(cacheKey, applyBrandNameCorrections(response.data, language));
        } catch (err) {
          const elapsedTime = Date.now() - startTime;
          const errorMessage = err.response?.data?.message || err.message;
          const statusCode = err.response?.status;

          // Create enhanced error object with timing and status info
          const enhancedError = {
            message: errorMessage,
            elapsedTime: elapsedTime,
            statusCode: statusCode,
            isTimeout: elapsedTime >= 60000, // 60 seconds
            isNoData: statusCode === 422 // Unprocessable Entity = no matching items
          };

          setError(enhancedError);
        } finally {
          setLoading(false);
        }
      };
  
      fetchData();
    }, [analysisData, tabKey, shouldFetch, trendsDataCache, updateTrendsCache, language, cacheKey, retryTrigger]);
  
    return { data: trendsDataCache[cacheKey], loading, error };
  };